import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ShieldAlert, CheckCircle, XCircle, Download, MessageCircle, ArrowRight, AlertTriangle } from 'lucide-react';
import GuideDownloadForm from '../components/GuideDownloadForm';
import { suspendedContent, suspendedSlugs } from '../data/suspendedPageContent';
import { usePageSeo } from '../hooks/usePageSeo';

const WA = 'https://wa.me/34613076099';
const DOMAIN = 'https://agencia-ppc.es';

const buildJsonLd = (c, language) => [
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: c.h1,
    description: c.metaDescription,
    inLanguage: language,
    url: `${DOMAIN}${suspendedSlugs[language]}`,
    author: { '@type': 'Person', name: 'Ulysse Berneau' },
    publisher: { '@type': 'Organization', name: 'Agencia PPC', url: DOMAIN },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: c.faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  },
];

const scrollToForm = () => document.getElementById('guide-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });

const Hero = ({ c }) => (
  <section className="relative pt-28 lg:pt-32 pb-16 bg-slate-900 overflow-hidden">
    <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-red-500/10 blur-3xl" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
      <div className="lg:pt-6">
        <div className="inline-flex items-center px-4 py-2 rounded-full bg-red-500/15 text-red-300 text-sm font-semibold mb-6">
          <ShieldAlert className="w-4 h-4 mr-2" />{c.badge}
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-6" data-testid="suspended-h1">{c.h1}</h1>
        <p className="text-lg text-slate-300 leading-relaxed mb-8">{c.intro}</p>
        <ul className="space-y-3">
          {c.heroPoints.map((p) => (
            <li key={p} className="flex items-center gap-3 text-slate-200"><CheckCircle className="w-5 h-5 text-green-400 shrink-0" />{p}</li>
          ))}
        </ul>
      </div>
      <Card id="guide-form" className="bg-white border-0 rounded-2xl shadow-2xl">
        <CardContent className="p-6 sm:p-8">
          <h2 className="text-xl font-bold text-slate-800 mb-5">{c.formTitle}</h2>
          <GuideDownloadForm location="seo-page" />
        </CardContent>
      </Card>
    </div>
  </section>
);

const Section = ({ s }) => (
  <section id={s.id} className="scroll-mt-28 mb-14" data-testid={`suspended-section-${s.id}`}>
    <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">{s.title}</h2>
    {s.text && <p className="text-slate-600 leading-relaxed mb-6">{s.text}</p>}
    {s.items && (
      <div className="space-y-4">
        {s.items.map((it) => (
          <div key={it.title} className="border-l-4 border-red-500 bg-slate-50 rounded-r-xl p-5">
            <h3 className="font-bold text-slate-900 mb-1">{it.title}</h3>
            <p className="text-slate-600 text-sm leading-relaxed">{it.desc}</p>
          </div>
        ))}
      </div>
    )}
    {s.note && <p className="mt-4 text-sm text-slate-500">{s.note}</p>}
    {s.steps && (
      <ol className="grid sm:grid-cols-2 gap-4">
        {s.steps.map((st) => (
          <li key={st.when} className="rounded-xl border border-slate-200 p-5">
            <span className="text-xs font-bold uppercase tracking-wider text-red-600">{st.when}</span>
            <h3 className="font-bold text-slate-900 mt-1 mb-1">{st.title}</h3>
            <p className="text-sm text-slate-600 leading-relaxed">{st.desc}</p>
          </li>
        ))}
      </ol>
    )}
    {s.list && (
      <ul className="space-y-3">
        {s.list.map((l) => {
          const bad = s.id.startsWith('err') || s.id === 'mistakes';
          const Icon = bad ? XCircle : CheckCircle;
          return (
            <li key={l} className="flex items-start gap-3 text-slate-700">
              <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${bad ? 'text-red-500' : 'text-green-500'}`} />{l}
            </li>
          );
        })}
      </ul>
    )}
  </section>
);

const GuideCta = ({ c }) => (
  <div className="my-14 rounded-2xl bg-red-50 border border-red-100 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-6" data-testid="suspended-guide-cta">
    <div className="flex-1">
      <h2 className="text-xl font-bold text-slate-900 mb-2">{c.guideCta.title}</h2>
      <p className="text-slate-600 text-sm leading-relaxed">{c.guideCta.text}</p>
    </div>
    <Button onClick={scrollToForm} className="bg-red-600 hover:bg-red-700 text-white font-semibold shrink-0" size="lg" data-testid="suspended-guide-cta-button">
      <Download className="w-5 h-5 mr-2" />{c.guideCta.button}
    </Button>
  </div>
);

const Faq = ({ c }) => (
  <section id="faq" className="scroll-mt-28 mb-14" data-testid="suspended-faq">
    <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">{c.faqTitle}</h2>
    <Accordion type="single" collapsible>
      {c.faq.map((f, i) => (
        <AccordionItem key={f.q} value={`q${i}`}>
          <AccordionTrigger className="text-left font-semibold text-slate-900" data-testid={`suspended-faq-${i}`}>{f.q}</AccordionTrigger>
          <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

const Help = ({ c, language }) => (
  <section className="rounded-2xl bg-slate-900 text-white p-6 sm:p-8" data-testid="suspended-help">
    <h2 className="text-2xl font-bold mb-3">{c.help.title}</h2>
    <p className="text-slate-300 mb-6">{c.help.text}</p>
    <div className="flex flex-col sm:flex-row gap-3">
      <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="suspended-whatsapp"
        onClick={() => window.dataLayer?.push({ event: 'click_whatsapp', click_location: 'suspended_page', language })}
        className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-[#25D366] hover:bg-[#1ebe5b] font-semibold transition-colors">
        <MessageCircle className="w-5 h-5 mr-2" />{c.help.whatsapp}
      </a>
      <Link to={`/${language}/compliance`} data-testid="suspended-compliance-link"
        className="inline-flex items-center justify-center px-6 py-3 rounded-lg border border-white/30 hover:bg-white/10 font-semibold transition-colors">
        {c.help.compliance}<ArrowRight className="w-4 h-4 ml-2" />
      </Link>
    </div>
  </section>
);

export default function SuspendedAccountPage() {
  const { language } = useLanguage();
  const c = suspendedContent[language] || suspendedContent.es;
  const jsonLd = useMemo(() => buildJsonLd(c, language), [c, language]);
  usePageSeo({ title: c.metaTitle, description: c.metaDescription, alternates: suspendedSlugs, jsonLd });

  return (
    <div className="min-h-screen bg-white" data-testid="suspended-page">
      <Hero c={c} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <nav className="mb-12 rounded-xl bg-slate-50 p-5" aria-label={c.toc}>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">{c.toc}</p>
          <ul className="space-y-1.5">
            {c.sections.map((s) => (
              <li key={s.id}><a href={`#${s.id}`} className="text-blue-600 hover:underline">{s.title}</a></li>
            ))}
            <li><a href="#faq" className="text-blue-600 hover:underline">{c.faqTitle}</a></li>
          </ul>
        </nav>
        {c.sections.slice(0, 2).map((s) => <Section key={s.id} s={s} />)}
        <GuideCta c={c} />
        {c.sections.slice(2).map((s) => <Section key={s.id} s={s} />)}
        <Faq c={c} />
        <Help c={c} language={language} />
        <p className="mt-10 text-xs text-slate-400 flex items-start gap-2"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{c.disclaimer}</p>
      </div>
    </div>
  );
}
